import {ITEM_CONTROL_DIMENSIONS} from '@PosterWhiteboard/poster/poster-item-controls.types';
import type {AnimationFrameStateObject, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.types';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type * as Fabric from '@postermywall/fabricjs-2';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import type {DeepPartial} from '@/global';

// General helpers over items and serialized item objects. Timing math over serialized item objects
// lives in item-timing.library.ts. Keep this module free of Fabric/canvas VALUE imports so the
// timeline UI, page-duration.library, and unit tests can depend on it without loading the editor's
// heavy item pipeline (type-only Fabric imports are fine — they're erased at compile time).

export function isVideoItemObject(itemObject: ItemObject): itemObject is VideoItemObject {
  return itemObject.gitype === ITEM_TYPE.VIDEO;
}

export function isTranscriptItemObject(itemObject: ItemObject): itemObject is TranscriptItemObject {
  return itemObject.gitype === ITEM_TYPE.TRANSCRIPT;
}

export function isSlideshowItemObject(itemObject: ItemObject): itemObject is SlideshowItemObject {
  return itemObject.gitype === ITEM_TYPE.SLIDESHOW;
}

export const getScaledManualHorizontalPadding = (scaleX: number | undefined): number => {
  return 2 * getScaledManualLeftPadding(scaleX);
};

export const getScaledManualVerticalPadding = (scaleY: number | undefined): number => {
  return 2 * getScaledManualTopPadding(scaleY);
};

export const getScaledManualLeftPadding = (scaleX: number | undefined): number => {
  return scaleX ? ITEM_CONTROL_DIMENSIONS.PMW_ITEM_LEGACY_PADDING / scaleX : 1;
};

export const getScaledManualTopPadding = (scaleY: number | undefined): number => {
  return scaleY ? ITEM_CONTROL_DIMENSIONS.PMW_ITEM_LEGACY_PADDING / scaleY : 1;
};

export const getCumulativeVCScaleY = (vc: Fabric.Group | Fabric.FabricObject | undefined): number => {
  return vc ? vc.scaleY * getCumulativeVCScaleY(vc.group) : 1;
};

export const getCumulativeVCScaleX = (vc: Fabric.Group | Fabric.FabricObject | undefined): number => {
  return vc ? vc.scaleX * getCumulativeVCScaleX(vc.group) : 1;
};

export const getStaticValuesForAnimatingItem = (item: Item): DeepPartial<AnimationFrameStateObject> => {
  const defaultValues: Record<string, number> = {};
  const values: DeepPartial<AnimationFrameStateObject> = {};
  if (item.hasIntroAnimation() && item.introAnimation.animation.isAnimationPlaying) {
    Object.assign(defaultValues, {...item.introAnimation.animation.onCompleteValues});
  }
  if (item.hasOutroAnimation() && item.outroAnimation.animation.isAnimationPlaying) {
    Object.assign(defaultValues, {...item.outroAnimation.animation.startingAnimationValues});
  }
  if ('left' in defaultValues) {
    Object.assign(values, {left: defaultValues.left});
  }
  if ('top' in defaultValues) {
    Object.assign(values, {top: defaultValues.top});
  }
  if ('scaleX' in defaultValues) {
    Object.assign(values, {scaleX: defaultValues.scaleX});
  }
  if ('scaleY' in defaultValues) {
    Object.assign(values, {scaleY: defaultValues.scaleY});
  }
  if ('angle' in defaultValues) {
    Object.assign(values, {angle: defaultValues.angle});
  }
  if ('opacity' in defaultValues) {
    Object.assign(values, {alpha: defaultValues.opacity});
  }
  return values;
};
